import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  DataGridPro,
  DataGridProProps,
  GridSortModel,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColDefExtended,
  GridCellParams,
  MuiEvent,
  GridSlots
} from '@mui/x-data-grid-pro';
import DispatchCard from '@components/DataGrid/Dispatch/Cells/DispatchCard';
import BrandLinearProgress from '@/Infrastructure/Components/Loading/BrandLinearProgress';
import { applyThemeGridSxOverrides } from '@/Infrastructure/Theme/AppThemeOverrides';
import { researchQuery } from '@api/Trinium/Endpoints/BoardQueries/types';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { getDispatchGridColDefs } from '@components/DataGrid/Dispatch/columns';
import { columnsManagementSlotProps, expandSubColumns } from '@components/DataGrid/subColumns';
import { GenericOverlay } from '@components/DataGrid/GenericNoRowsOverlay';
import useResearchQuery from '@hooks/useResearchQuery';
import ResearchGridToolbar from './ResearchGridToolbar';
import { useAppDispatch } from '@reduxStore';
import { getApiErrorMessage } from '@api/getApiError';
import { queueUncontrolledToast } from '@toast';
import DynamicSelectContainer from '@components/DynamicSelectContainer';
import EditableContainer from '../EditableContainer';
import ApplicationSpinner from '@/Infrastructure/Components/Loading/ApplicationSpinner';
import { EditableCellRef } from '../types';

import styles from '@components/DataGrid/RenderCell.module.css';

const ROW_CLASS_NAME = styles['custom-row'];
/**
 * Rows with no despatch leg (cancelled, closed/purged, or legs deleted outright) come back with no
 * `legId`, so they would all collide on the same id and DataGridPro would drop all but one. Real legs
 * keep their `legId` (so the existing selection/action plumbing that treats row ids as legIds is
 * unaffected); a legless row falls back to a unique negative `-csnId` that can never collide with a
 * real (positive) legId.
 */
const getRowId = (row: DispatchLeg) => (!row.legId ? -row.csnId : row.legId);
const getRowClassName = () => ROW_CLASS_NAME;

/**
 * Research (Dispatch) Grid component implementing the MUI DataGridPro
 */
export default function ResearchGrid() {
  const appDispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isFetching, data, paramsEmpty, isError, error } = useResearchQuery();
  /** Group that is currently viewable */
  const [activeGroup, setActiveGroup] = useState(0);
  const [sortingModel, setSortingModel] = useState<GridSortModel>([{ field: 'orderNumber', sort: 'asc' }]);
  const [editCellAnchorEl, setEditCellAnchorEl] = useState<HTMLElement | SVGElement | null>(null);
  const [editableContent, setEditableContent] = useState<ReactNode | null>(null);
  const editableRef = useRef<EditableCellRef>(null);

  /** Retrieve the Data Grid columns - memoized. Mobile cards render from this unexpanded set. */
  const gridColumns = useMemo(() => getDispatchGridColDefs(researchQuery), []);

  /** Desktop grid columns expanded with hidden sub-sort companion columns */
  const { desktopColumns, columnVisibilityModel } = useMemo(() => {
    const { columns, hiddenFields } = expandSubColumns(gridColumns);
    return {
      desktopColumns: columns,
      columnVisibilityModel: { __check__: true, ...hiddenFields }
    };
  }, [gridColumns]);

  useEffect(() => {
    if (isError) {
      const deserializedError = getApiErrorMessage(error);
      appDispatch(
        queueUncontrolledToast({
          type: 'message',
          severity: 'error',
          message: deserializedError
        })
      );
    }
  }, [appDispatch, error, isError]);

  const changeSortModel = useCallback((gridSortModel: GridSortModel) => {
    setSortingModel(gridSortModel);
  }, []);

  const onCellClickHandler = useCallback((params: GridCellParams<DispatchLeg>, event: MuiEvent<React.MouseEvent<HTMLElement>>) => {
    const extendedColDef: GridColDefExtended<DispatchLeg> = params.colDef;

    if (extendedColDef.editable && extendedColDef.renderEditableCell) {
      event.defaultMuiPrevented = true;
      const content = extendedColDef.renderEditableCell(params.row, editableRef);
      if (content) {
        setEditCellAnchorEl(event.currentTarget);
        setEditableContent(content);
      }
    }
  }, []);

  const onMobileCellClickHandler = useCallback(
    (target: EventTarget & HTMLElement, columnDetails: GridColDefExtended<DispatchLeg>, row: DispatchLeg) => {
      setEditCellAnchorEl(target);
      setEditableContent(columnDetails.renderEditableCell?.(row, editableRef));
    },
    []
  );

  const onEditCellClose = useCallback(() => {
    editableRef.current?.saveChanges('row');
    setEditableContent(null);
    editableRef.current = null;
  }, []);

  const closeEditableCell = useCallback(() => {
    setEditCellAnchorEl(null);
  }, []);

  /**
   * Mobile overrides for the DataGrid to alter the styling on lower resolution screens.
   * Any properties that are overridden need to also be described in the DataGrid render
   * to ensure they are changed back when exiting Mobile resolutions.
   */
  const mobileGridProps: Partial<DataGridProProps<DispatchLeg>> = useMemo(
    () => ({
      rowHeight: 200,
      columns: [
        {
          ...GRID_CHECKBOX_SELECTION_COL_DEF
        },
        {
          field: 'Mobile Card',
          type: 'actions',
          flex: 1,
          renderCell: ({ row }: { row: DispatchLeg }) => (
            <DispatchCard
              key={getRowId(row)}
              leg={row}
              gridColumnDetails={gridColumns}
              gridPinnedColumns={researchQuery.configuration.pinnedCols}
              activeGroup={activeGroup}
              setActiveGroup={setActiveGroup}
              renderEditable={onMobileCellClickHandler}
            />
          )
        }
      ],
      pinnedColumns: {},
      columnHeaderHeight: 0,
      columnVisibilityModel: {
        __check__: false
      },
      onCellClick: undefined,
      sx: {
        ...applyThemeGridSxOverrides(),
        '& .MuiDataGrid-columnHeaders': {
          borderBottom: 'none'
        },
        '& .MuiDataGrid-cell': {
          border: 'none'
        },
        '& .MuiDataGrid-row.Mui-hovered': {
          backgroundColor: 'inherit'
        },
        '& .MuiDataGrid-row:hover': {
          backgroundColor: 'inherit'
        },
        '& .MuiDataGrid-row.Mui-selected': {
          backgroundColor: 'transparent'
        },
        '& .MuiDataGrid-row.Mui-selected.Mui-hovered': {
          backgroundColor: 'transparent'
        },
        '& .MuiDataGrid-row.Mui-selected:hover': {
          backgroundColor: 'transparent'
        }
      }
    }),
    [activeGroup, gridColumns, onMobileCellClickHandler]
  );

  /** Memoized slots to prevent re-renders */
  const slots = useMemo<Partial<GridSlots>>(
    () => ({
      loadingOverlay: BrandLinearProgress,
      noRowsOverlay: () => <GenericOverlay overlayText={!paramsEmpty ? 'No Legs Found.' : 'Execute a Query to Search Legs.'} />,
      toolbar: () => <ResearchGridToolbar />
    }),
    [paramsEmpty]
  );

  /** Memoized sx styles to prevent re-renders */
  const sx = useMemo<DataGridProProps['sx']>(
    () => ({
      ...applyThemeGridSxOverrides(),
      // Performance optimizations for scroll rendering
      '& .MuiDataGrid-virtualScroller': {
        willChange: 'transform',
        contain: 'strict'
      },
      '& .MuiDataGrid-row': {
        contain: 'layout style paint'
      },
      '& .MuiDataGrid-row.Mui-selected': {
        backgroundColor: theme.palette.grey[400]
      },
      [`& .MuiDataGrid-row.Mui-selected .${styles['pinned-column']}`]: {
        backgroundColor: `${theme.palette.grey[500]} !important`
      },
      '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedRight': {
        backgroundColor: `${theme.palette.grey[500]} !important`
      },
      '& .MuiDataGrid-row.Mui-selected.Mui-hovered': {
        backgroundColor: theme.palette.action.hover
      },
      '& .MuiDataGrid-row.Mui-selected:hover': {
        backgroundColor: theme.palette.action.hover
      },
      '& .MuiDataGrid-row.Mui-selected .row-menu-btn': {
        visibility: 'visible'
      },
      '& .MuiDataGrid-row:hover .row-menu-btn': {
        visibility: 'visible'
      },
      '& .MuiDataGrid-row:hover': {
        backgroundColor: theme.palette.action.hover
      }
    }),
    [theme.palette.action.hover, theme.palette.grey]
  );

  /** Dynamic buffer sizing - larger datasets get bigger buffers for smoother scrolling */
  const rowBufferPx = useMemo(() => {
    if (data.length > 500) return 3000;
    if (data.length > 250) return 2000;
    return 1500;
  }, [data.length]);

  return (
    <>
      <DataGridPro
        key={`research-${String(isMobile)}`}
        rows={data}
        loading={isFetching}
        rowHeight={50}
        rowBufferPx={rowBufferPx}
        columnBufferPx={500}
        disableVirtualization={data.length <= 250}
        columns={desktopColumns}
        pinnedColumns={researchQuery.configuration.pinnedCols}
        columnHeaderHeight={50}
        getRowId={getRowId}
        getRowClassName={getRowClassName}
        checkboxSelection
        sortModel={sortingModel}
        onSortModelChange={changeSortModel}
        onCellClick={onCellClickHandler}
        slots={slots}
        slotProps={columnsManagementSlotProps}
        sx={sx}
        columnVisibilityModel={columnVisibilityModel}
        disableRowSelectionOnClick
        {...(isMobile ? mobileGridProps : {})}
      />
      <DynamicSelectContainer
        anchorEl={editCellAnchorEl}
        setAnchorEl={setEditCellAnchorEl}
        anchorOptions={{
          anchorOrigin: {
            vertical: 'center',
            horizontal: 'center'
          },
          transformOrigin: {
            vertical: 'center',
            horizontal: 'center'
          }
        }}
        onTransitionExitedCallback={onEditCellClose}>
        <EditableContainer
          editableRef={editableRef}
          onClose={closeEditableCell}>
          {editableContent ? (
            editableContent
          ) : (
            <ApplicationSpinner
              height={24}
              spinnerSize={14}
            />
          )}
        </EditableContainer>
      </DynamicSelectContainer>
    </>
  );
}
